import { Box, VStack, Divider } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { STEPS, SECONDARY_NAV, HOME_NAV, ZARA_NAV, viewerIsSuperAdmin, type StepStatus } from '../routes';
import { useZaraEnabled } from '../zara/config';
import { useAuth } from '../auth/AuthContext';
import { useDetectReviewCount } from '../brand/useDetectReviewCount';
import { NavItem, SecondaryNavItem, SidebarShell, ZaraNavItem } from './sidebarChrome';
import { isNavIaV2Enabled } from './navIaV2/flag';
import { NavIaV2Sidebar } from './navIaV2/NavIaV2Sidebar';

// Fixed 240px left sidebar — Reach Social mark at top, four pipeline
// steps as nav links with status badges, sign-out at the bottom
// (Phase 3 wires the sign-out button to actual auth context).
//
// VITE_NAV_IA_V2_ENABLED === 'true' swaps the link lists for the /v2
// groups. Any other value keeps the tree below. The flag is global:
// it does not read a workspace or tenant.

type Props = {
  stepStatuses: Record<string, StepStatus>;
};

export function Sidebar({ stepStatuses }: Props) {
  if (isNavIaV2Enabled()) return <NavIaV2Sidebar />;
  return <LegacySidebar stepStatuses={stepStatuses} />;
}

function LegacySidebar({ stepStatuses }: Props) {
  const auth = useAuth();
  // Pending-detect-drafts count drives the red badge on the Detect
  // Review nav item — gives operators a quick "you have N items to
  // review" signal without needing to click in. Polls every 30s,
  // pauses on tab hide.
  const detectReviewCount = useDetectReviewCount();
  const zaraEnabled = useZaraEnabled();
  return (
    <SidebarShell>
      <VStack as="nav" align="stretch" spacing={1} mt={6}>
        {/* Home — unnumbered, above the numbered pipeline flow. It's the
            agent chat, not a step; operators land here by default and
            return to it whenever they want to ask a question. */}
        <NavLink to={HOME_NAV.path} style={{ textDecoration: 'none' }}>
          {({ isActive }) => <NavItem icon={<MessageSquare size={14} />} label={HOME_NAV.label} description={HOME_NAV.description} isActive={isActive} />}
        </NavLink>
        {zaraEnabled && (
          <>
            <Divider my={2} borderColor="brand.border" />
            <NavLink to={ZARA_NAV.path} style={{ textDecoration: 'none' }} data-testid="zara-nav">
              {({ isActive }) => <ZaraNavItem isActive={isActive} />}
            </NavLink>
          </>
        )}
        {STEPS.map((step, i) => {
          const status = stepStatuses[step.key] ?? 'pending';
          return (
            <NavLink key={step.key} to={step.path} style={{ textDecoration: 'none' }}>
              {({ isActive }) => <NavItem index={i + 1} label={step.label} description={step.description} status={status} isActive={isActive} />}
            </NavLink>
          );
        })}
      </VStack>

      <Box mt="auto">
        <Divider my={4} />
        <VStack as="nav" align="stretch" spacing={0.5}>
          {SECONDARY_NAV
            .filter(item => !item.adminOnly || viewerIsSuperAdmin(auth))
            .map(item => {
              // Detect Review gets a pending-count badge so operators see
              // "you have N items to review" without entering the page.
              const badgeCount = item.path === '/detect' ? detectReviewCount : 0;
              return (
                <NavLink key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                  {({ isActive }) => <SecondaryNavItem label={item.label} isActive={isActive} badgeCount={badgeCount} />}
                </NavLink>
              );
            })}
        </VStack>
      </Box>
    </SidebarShell>
  );
}
